import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import flytLogo from "@/assets/flyt-logo.svg";

function getPasswordRequirements(p: string) {
  return {
    length: p.length >= 8,
    lowercase: /[a-z]/.test(p),
    uppercase: /[A-Z]/.test(p),
    number: /[0-9]/.test(p),
  };
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  const req = getPasswordRequirements(password);

  useEffect(() => {
    // Listen for the PASSWORD_RECOVERY event from the URL token
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setReady(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Passordet må ha minst 8 tegn");
      return;
    }
    if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
      toast.error("Passordet må inneholde stor bokstav, liten bokstav og tall");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passordene stemmer ikke overens");
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Passordet er oppdatert!");
      navigate("/dashboard", { replace: true });
    }
  }

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-sm text-center shadow-md rounded-2xl">
          <CardHeader>
            <img src={flytLogo} alt="Flyt" className="mx-auto mb-2 h-14 w-auto" />
            <CardTitle className="text-2xl">Tilbakestill passord</CardTitle>
            <CardDescription>
              Venter på bekreftelse… Hvis du ikke ble sendt hit fra en e-post, prøv å{" "}
              <a href="/forgot-password" className="text-primary underline">be om ny lenke</a>.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm shadow-md rounded-2xl">
        <CardHeader className="text-center">
          <img src={flytLogo} alt="Flyt" className="mx-auto mb-2 h-14 w-auto" />
          <CardTitle className="text-2xl">Nytt passord</CardTitle>
          <CardDescription>Velg et nytt passord for kontoen din.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="new-password" className="text-sm font-medium">
                Nytt passord
              </label>
              <PasswordInput
                id="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
                placeholder="Velg et passord"
              />
              <ul className="text-xs text-muted-foreground space-y-1">
                <li className={req.length ? "text-muted-foreground" : "text-destructive"}>
                  {req.length ? "✓" : "○"} Minst 8 tegn
                </li>
                <li className={req.lowercase ? "text-muted-foreground" : "text-destructive"}>
                  {req.lowercase ? "✓" : "○"} Minst én liten bokstav (a–z)
                </li>
                <li className={req.uppercase ? "text-muted-foreground" : "text-destructive"}>
                  {req.uppercase ? "✓" : "○"} Minst én stor bokstav (A–Z)
                </li>
                <li className={req.number ? "text-muted-foreground" : "text-destructive"}>
                  {req.number ? "✓" : "○"} Minst ett tall (0–9)
                </li>
              </ul>
            </div>
            <div className="space-y-2">
              <label htmlFor="confirm-new-password" className="text-sm font-medium">
                Bekreft passord
              </label>
              <PasswordInput
                id="confirm-new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
                placeholder="Skriv passordet på nytt"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Oppdaterer…" : "Lagre nytt passord"}
            </Button>
          </CardContent>
        </form>
      </Card>
    </div>
  );
}
